import { useCallback, useEffect, useMemo, useState } from 'react';

import { Box } from '@mui/material';

import { useGetDatesQuery, useGetTypicalMonthsQuery } from 'state/apiSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectAnalysisEndDate,
  selectAnalysisStartDate,
  setAnalysisEndDate,
  setAnalysisStartDate,
} from 'state/originDestinationSlices';

import dayjs from 'dayjs';
import DateRangePicker from 'features/common/DateTimeSelector/DateRangePicker';
import DateTimePickerWrapper from 'features/common/DateTimeSelector/DateTimePickerWrapper';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { StlButton } from 'features/common/StlButton/StlButton';
import StlConfirmButtons from 'features/common/StlConfirmButtons';
import StlPopoverWrapper from 'features/common/StlPopoverWrapper/StlPopoverWrapper';
import { dateToString, parseDate } from 'features/common/datetimeUtils';
import {
  formatDateToMonthYear,
  formatDateToString,
  getMinDate,
} from 'features/user_admin/SpecialDateRequest/utils';
import { formatDateShort } from 'features/user_admin/common';

import { selectProject } from '../../../state/workflowSlice';

type TempRange = {
  start: string | undefined;
  end: string | undefined;
};

const INITIAL_RANGE = {
  start: undefined,
  end: undefined,
};

const MAX_NUMB_DAYS = 31;

export const getMinSelectableDate = (allowedMonths: string[]): dayjs.Dayjs => {
  const twoYearsAgo = getMinDate().startOf('month');
  const firstInRange = allowedMonths.find(
    (month) => !dayjs(month).startOf('month').isBefore(twoYearsAgo, 'month'),
  );
  return firstInRange ? dayjs(firstInRange).startOf('month') : twoYearsAgo;
};

export default function ODDateRangePicker() {
  const dispatch = useAppDispatch();

  const project = useAppSelector(selectProject);
  const analysisStartDate = useAppSelector(selectAnalysisStartDate);
  const analysisEndDate = useAppSelector(selectAnalysisEndDate);

  const [open, setOpen] = useState<boolean>(false);
  const [tempRange, setTempRange] = useState<TempRange>(INITIAL_RANGE);
  const [key, setKey] = useState<number>(0);

  const { data: typicalMonthsInfo } = useGetTypicalMonthsQuery(project);
  const { data: datesInfo = [] } = useGetDatesQuery(project);

  const allowedMonths = useMemo(() => {
    const months = new Set<string>();

    typicalMonthsInfo.forEach((el) => {
      months.add(formatDateToMonthYear(el.typicalMonth));
    });

    datesInfo.forEach((el) => {
      months.add(formatDateToMonthYear(el.date));
    });
    return [...months].sort();
  }, [typicalMonthsInfo, datesInfo]);

  const filterDate = useCallback(
    (date: Date) => {
      if (!allowedMonths.includes(formatDateToMonthYear(date))) return false;
      if (tempRange.start && !tempRange.end) {
        return (
          formatDateToMonthYear(date) === formatDateToMonthYear(tempRange.start)
        );
      }
      return true;
    },
    [allowedMonths, tempRange.start, tempRange.end],
  );

  const handleApply = () => {
    const { start, end } = tempRange;
    if (start && end) {
      dispatch(setAnalysisEndDate(end));
      dispatch(setAnalysisStartDate(start));
    }
    setOpen(false);
    resetTempRange();
  };

  const handleCancel = () => {
    setOpen(false);
    resetTempRange();
  };

  const maxDayjs = useMemo(() => {
    const max = dayjs().subtract(2, 'day');
    if (!allowedMonths) return undefined;
    if (allowedMonths.includes(formatDateToMonthYear(max))) return max;

    // filter out future months
    const mostRecent = allowedMonths
      .filter((m) => dayjs(m).isBefore(dayjs(), 'month'))
      .sort();

    // get the last day of the latest month that is not in the future
    return dayjs(mostRecent[mostRecent.length - 1]).endOf('month');
  }, [allowedMonths]);

  const minDayjs = useMemo(
    () => getMinSelectableDate(allowedMonths),
    [allowedMonths],
  );

  // set up a default state
  useEffect(() => {
    if (!analysisStartDate && !analysisEndDate && maxDayjs) {
      dispatch(setAnalysisStartDate(dateToString(maxDayjs.subtract(1, 'day'))));
      dispatch(setAnalysisEndDate(dateToString(maxDayjs)));
    }
  }, [maxDayjs]);

  const getButtonTitle = () => {
    const rangeStart = tempRange.start ?? analysisStartDate;
    const rangeEnd = tempRange.start ? tempRange.end : analysisEndDate;
    if (rangeStart || rangeEnd)
      return `${formatDateShort(rangeStart)} - ${rangeEnd ? formatDateShort(rangeEnd) : ''}`;
    return '';
  };

  const updateRangeTemp = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null]) => {
    const [start, end] = dates;
    setTempRange({
      start: start ? formatDateToString(start) : undefined,
      end: end ? formatDateToString(end) : undefined,
    });
  };

  const resetTempRange = () => {
    setTempRange(INITIAL_RANGE);
    setKey((k) => k + 1);
  };

  return (
    <StlPopoverWrapper
      open={open}
      setOpen={setOpen}
      endIcon={<FontAwesomeIcon name="calendar-day" />}
      renderLabel={getButtonTitle}
      onClose={resetTempRange}
    >
      <DateTimePickerWrapper
        title="Select date"
        height="auto"
        width="auto"
        component={
          <DateRangePicker
            key={key}
            startValue={parseDate(analysisStartDate)}
            endValue={parseDate(analysisEndDate)}
            monthsShown={1}
            maxDays={MAX_NUMB_DAYS}
            maxDate={maxDayjs}
            minDate={minDayjs}
            filterDate={filterDate}
            onChange={updateRangeTemp}
          />
        }
      />
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" justifyContent="flex-start" gap={0.25}>
          <StlButton
            variant="text"
            value="Reset"
            width={52}
            handleClick={resetTempRange}
          />
        </Box>
        <StlConfirmButtons
          width={64}
          disabled={false}
          handleApply={handleApply}
          handleCancel={handleCancel}
        />
      </Box>
    </StlPopoverWrapper>
  );
}
